import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { ExamScore } from '@/types';
import type { ExamScoreSession } from '@/lib/scores';
import { buildExamTitle } from '@/lib/scores';
import { EXAM_TYPE_TONE } from '@/lib/subjects';
import { inputClass } from '@/components/base/Field';

interface ExamSessionCardProps {
  session: ExamScoreSession;
  onChange: () => void;
}

export default function ExamSessionCard({ session, onChange }: ExamSessionCardProps) {
  const [rows, setRows] = useState<ExamScore[]>(session.rows);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setRows(session.rows);
    setConfirmDelete(false);
  }, [session.rows]);

  const changed = rows.filter((r) => {
    const origin = session.rows.find((o) => o.id === r.id);
    return (
      origin &&
      (origin.score !== r.score ||
        origin.standard_score !== r.standard_score ||
        origin.percentile !== r.percentile ||
        origin.rank_grade !== r.rank_grade)
    );
  });
  const dirty = changed.length > 0;

  const setRow = (
    id: string,
    key: 'score' | 'standard_score' | 'percentile' | 'rank_grade',
    value: string,
  ) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)));
  };

  const title = buildExamTitle(session);

  const handleSave = async () => {
    if (!dirty) return;
    setSaving(true);
    setError('');
    try {
      const results = await Promise.all(
        changed.map((r) =>
          supabase
            .from('exam_scores')
            .update({
              score: r.score,
              standard_score: r.standard_score,
              percentile: r.percentile,
              rank_grade: r.rank_grade,
            })
            .eq('id', r.id),
        ),
      );
      const failed = results.find((res) => res.error);
      if (failed?.error) throw failed.error;
      onChange();
    } catch {
      setError('성적 저장에 실패했어요.');
    } finally {
      setSaving(false);
    }
  };

  const removeRow = async (id: string) => {
    setError('');
    try {
      const { error: deleteError } = await supabase.from('exam_scores').delete().eq('id', id);
      if (deleteError) throw deleteError;
      onChange();
    } catch {
      setError('과목 삭제에 실패했어요.');
    }
  };

  const removeSession = async () => {
    setSaving(true);
    setError('');
    try {
      const ids = session.rows.map((r) => r.id);
      const { error: deleteError } = await supabase.from('exam_scores').delete().in('id', ids);
      if (deleteError) throw deleteError;
      onChange();
    } catch {
      setError('시험 삭제에 실패했어요.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="overflow-hidden rounded-xl border border-background-200 bg-background-50">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-background-100 px-4 py-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-left"
        >
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
              EXAM_TYPE_TONE[session.examType] ?? 'bg-background-200 text-foreground-700'
            }`}
          >
            {session.examType}
          </span>
          <span className="text-sm font-bold text-foreground-950">{title}</span>
          <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-semibold text-secondary-900">
            {rows.length}과목
          </span>
          <i
            className={`ml-auto text-lg text-foreground-400 ${
              expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
            }`}
          ></i>
        </button>
        <div className="flex items-center gap-2">
          {dirty && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 rounded-md bg-primary-500 px-3 py-1.5 text-xs font-semibold text-background-50 transition hover:bg-primary-600 disabled:opacity-50 whitespace-nowrap"
            >
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
              변경사항 저장
            </button>
          )}
          {confirmDelete ? (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={removeSession}
                disabled={saving}
                className="rounded-md bg-accent-500 px-3 py-1.5 text-xs font-semibold text-background-50 transition hover:bg-accent-600 disabled:opacity-50 whitespace-nowrap"
              >
                삭제 확인
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-foreground-700 transition hover:bg-background-200 whitespace-nowrap"
              >
                취소
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              aria-label="시험 삭제"
              className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-400 transition hover:bg-background-200 hover:text-accent-700"
            >
              <i className="ri-delete-bin-6-line"></i>
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <>
          <div className="hidden grid-cols-[1.3fr_0.8fr_0.8fr_0.8fr_1.3fr_2.5rem] gap-3 border-y border-background-200 bg-background-50 px-4 py-2 text-xs font-semibold text-foreground-500 sm:grid">
            <span>과목</span>
            <span>원점수</span>
            <span>표준점수</span>
            <span>백분위</span>
            <span>등급 / 석차</span>
            <span></span>
          </div>

          <div className="divide-y divide-background-200">
            {rows.map((r) => (
              <div
                key={r.id}
                className="grid grid-cols-1 gap-2 px-4 py-2.5 sm:grid-cols-[1.3fr_0.8fr_0.8fr_0.8fr_1.3fr_2.5rem] sm:items-center sm:gap-3"
              >
                <span className="text-sm font-semibold text-foreground-950">{r.subject || '-'}</span>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-medium text-foreground-500 sm:hidden">원점수</span>
                  <input
                    type="text"
                    value={r.score ?? ''}
                    onChange={(e) => setRow(r.id, 'score', e.target.value)}
                    placeholder="예: 92"
                    className={`${inputClass} text-sm`}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-medium text-foreground-500 sm:hidden">표준점수</span>
                  <input
                    type="text"
                    value={r.standard_score ?? ''}
                    onChange={(e) => setRow(r.id, 'standard_score', e.target.value)}
                    placeholder="예: 128"
                    className={`${inputClass} text-sm`}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-medium text-foreground-500 sm:hidden">백분위</span>
                  <input
                    type="text"
                    value={r.percentile ?? ''}
                    onChange={(e) => setRow(r.id, 'percentile', e.target.value)}
                    placeholder="예: 96"
                    className={`${inputClass} text-sm`}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-medium text-foreground-500 sm:hidden">등급 / 석차</span>
                  <input
                    type="text"
                    value={r.rank_grade ?? ''}
                    onChange={(e) => setRow(r.id, 'rank_grade', e.target.value)}
                    placeholder="예: 2등급 / 15등"
                    className={`${inputClass} text-sm`}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => removeRow(r.id)}
                  aria-label={`${r.subject} 삭제`}
                  className="flex h-8 w-8 items-center justify-center justify-self-end rounded-md text-foreground-400 transition hover:bg-background-200 hover:text-accent-700"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {error && (
        <div className="flex items-center gap-2 border-t border-accent-200 bg-accent-100/70 px-4 py-2 text-xs text-accent-800">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}
    </div>
  );
}